import React, { useEffect, useId, useRef, useState } from 'react';
import {
  ADMIN_PROSPECT_ANALYSIS_ADDRESS_PREFIX_MIN,
  ADMIN_PROSPECT_ANALYSIS_TRANSFER_SECTION_MUNICIPAL,
  ADMIN_PROSPECT_ANALYSIS_TRANSFER_SECTION_OWNER_EXPENSES,
  buildCreateAdminProspectAnalysisPropertyRequest,
  buildLinkAdminProspectAnalysisPropertyRequest,
  buildListAdminProspectAnalysisLinkCandidatesRequest,
  buildProspectAnalysisCreatePreview,
  createAdminProspectAnalysisConversionWriteAttempt,
  fingerprintAdminProspectAnalysisConversionCreatePayload,
  fingerprintAdminProspectAnalysisConversionLinkPayload,
  resolveAdminProspectAnalysisLinkCandidatesPrefix,
} from '../../services/admin/prospectAnalysisConversionRequest';
import {
  createAdminProspectAnalysisProperty,
  linkAdminProspectAnalysisProperty,
  listAdminProspectAnalysisLinkCandidates,
} from '../../services/admin/prospectsService';
import { createAdminProspectRequestGate } from '../../services/admin/prospectTargetingFilters';
import { formatCadCentsForDisplay } from '../../utils/publicCadMoney';
import {
  isConversionWriteUncertainError,
  runConversionWriteAttempt,
  shouldApplyConversionResult,
} from './adminProspectAnalysisConversionController';
import {
  ADMIN_DATE_PLACEHOLDER,
  adminProspectAnalysisConversionErrorKey,
  classifyAdminCallableError,
  formatAdminProspectUnits,
} from './adminProspectDisplay';

/**
 * Create or link a Rentalyzer analysis property from an Immo 360 prospect.
 * Destination account is server-derived — never send client owner overrides.
 * Create/link writes are super_admin-only at the gate; this dialog assumes caller already gated.
 */
export default function AdminProspectAnalysisConversionDialog({
  open,
  prospect,
  visitId = null,
  authUid = null,
  language = 'fr',
  t,
  onClose,
  onConverted,
}) {
  const titleId = useId();
  const errorId = useId();
  const tabCreateId = useId();
  const tabLinkId = useId();
  const panelCreateId = useId();
  const panelLinkId = useId();
  const firstRef = useRef(null);
  const listGateRef = useRef(createAdminProspectRequestGate());
  const saveGateRef = useRef(createAdminProspectRequestGate());
  const writeAttemptRef = useRef(createAdminProspectAnalysisConversionWriteAttempt());
  const mountedRef = useRef(true);
  const openProspectIdRef = useRef('');
  const openVisitIdRef = useRef(null);
  const openAuthUidRef = useRef(null);
  /** Mode that owns the frozen uncertain attempt — retry always uses this path. */
  const frozenModeRef = useRef(null);

  const [mode, setMode] = useState('create');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uncertainRetry, setUncertainRetry] = useState(false);
  const [transferMunicipal, setTransferMunicipal] = useState(false);
  const [transferOwnerExpenses, setTransferOwnerExpenses] = useState(false);
  const [addressDraft, setAddressDraft] = useState('');
  const [appliedAddressPrefix, setAppliedAddressPrefix] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [listStatus, setListStatus] = useState('idle');
  const [listError, setListError] = useState('');
  const [listContinuationEmpty, setListContinuationEmpty] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState('');

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    openProspectIdRef.current = String(prospect?.id || '').trim();
    openVisitIdRef.current = visitId;
    openAuthUidRef.current = authUid;
    // New manager instance per visit — in-flight submits keep their captured instance.
    writeAttemptRef.current = createAdminProspectAnalysisConversionWriteAttempt();
    frozenModeRef.current = null;
    setMode('create');
    setSaving(false);
    setError('');
    setUncertainRetry(false);
    setTransferMunicipal(false);
    setTransferOwnerExpenses(false);
    setAddressDraft('');
    setAppliedAddressPrefix('');
    setCandidates([]);
    setNextPageToken(null);
    setListStatus('idle');
    setListError('');
    setListContinuationEmpty(false);
    setSelectedPropertyId('');
    listGateRef.current.next();
    saveGateRef.current.next();
    const id = window.setTimeout(() => firstRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open, prospect?.id, visitId, authUid]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !saving) {
        event.preventDefault();
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, saving, onClose]);

  if (!open || !prospect) return null;

  const version = prospect.updatedAtVersion || prospect.updatedAt || null;
  const preview = buildProspectAnalysisCreatePreview(prospect);
  const createReady = Boolean(
    version &&
      preview.transferSections.address.available &&
      preview.address?.trim() &&
      preview.city?.trim() &&
      preview.province?.trim() &&
      preview.postalCode?.trim(),
  );
  const linkReady = Boolean(version && selectedPropertyId);
  const controlsLocked = saving || uncertainRetry;

  const selectedTransferIds = () => {
    const ids = [];
    if (transferMunicipal) ids.push(ADMIN_PROSPECT_ANALYSIS_TRANSFER_SECTION_MUNICIPAL);
    if (transferOwnerExpenses) ids.push(ADMIN_PROSPECT_ANALYSIS_TRANSFER_SECTION_OWNER_EXPENSES);
    return ids;
  };

  const stillOwnsSession = (requestProspectId, requestVisitId, requestAuthUid) =>
    shouldApplyConversionResult({
      requestProspectId,
      requestVisitId,
      activeProspectId: String(prospect?.id || openProspectIdRef.current || '').trim(),
      activeVisitId: visitId ?? openVisitIdRef.current,
      requestAuthUid,
      activeAuthUid: authUid ?? openAuthUidRef.current,
    }) &&
    shouldApplyConversionResult({
      requestProspectId,
      requestVisitId,
      activeProspectId: openProspectIdRef.current,
      activeVisitId: openVisitIdRef.current,
      requestAuthUid,
      activeAuthUid: openAuthUidRef.current,
    });

  const displayValue = (value) => {
    if (value == null || value === '') return ADMIN_DATE_PLACEHOLDER;
    return String(value);
  };

  const formatCents = (cents) => formatCadCentsForDisplay(cents, language) || ADMIN_DATE_PLACEHOLDER;

  const loadCandidates = async ({ append = false, pageToken = null } = {}) => {
    const requestId = listGateRef.current.next();
    setListStatus(append ? 'loading-more' : 'loading');
    setListError('');
    setListContinuationEmpty(false);
    const appliedForRequest = append ? appliedAddressPrefix : undefined;
    const { prefix } = resolveAdminProspectAnalysisLinkCandidatesPrefix({
      append,
      draftFilter: addressDraft,
      appliedAddressPrefix: appliedForRequest ?? appliedAddressPrefix,
      minLength: ADMIN_PROSPECT_ANALYSIS_ADDRESS_PREFIX_MIN,
    });
    const addressPrefixForRequest = append ? appliedAddressPrefix || undefined : prefix;
    if (!append) {
      setSelectedPropertyId('');
      setAppliedAddressPrefix(prefix || '');
    }
    const request = buildListAdminProspectAnalysisLinkCandidatesRequest({
      pageToken: pageToken || undefined,
      addressPrefix: addressPrefixForRequest,
    });
    try {
      const data = await listAdminProspectAnalysisLinkCandidates(request);
      if (!mountedRef.current || !listGateRef.current.isCurrent(requestId)) return;
      const page = Array.isArray(data?.properties) ? data.properties : [];
      setCandidates((current) => (append ? [...current, ...page] : page));
      setNextPageToken(data?.nextPageToken || null);
      setListStatus('ready');
      if (append && page.length === 0) {
        setListContinuationEmpty(true);
      }
    } catch (err) {
      if (!mountedRef.current || !listGateRef.current.isCurrent(requestId)) return;
      setListError(t(adminProspectAnalysisConversionErrorKey(classifyAdminCallableError(err))));
      setListStatus('error');
      if (!append) {
        setCandidates([]);
        setNextPageToken(null);
      }
    }
  };

  const handleModeChange = (nextMode) => {
    if (controlsLocked || nextMode === mode) return;
    setMode(nextMode);
    setError('');
    if (nextMode === 'link' && listStatus === 'idle') {
      void loadCandidates();
    }
  };

  const handleConvertedSuccess = (result, activeMode, ownership, submittedOperationId = null) => {
    if (!stillOwnsSession(ownership.prospectId, ownership.visitId, ownership.authUid)) return;
    onConverted?.({
      propertyId: result?.propertyId || null,
      prospect: result?.prospect || null,
      mode: activeMode,
      idempotent: result?.idempotent === true,
      visitId: ownership.visitId,
      prospectId: ownership.prospectId,
      authUid: ownership.authUid,
    });
    // Only clear this session's attempt — never wipe a newer visit's frozen op.
    if (submittedOperationId) {
      writeAttemptRef.current.discardIfOperation(submittedOperationId);
    } else {
      writeAttemptRef.current.discard();
    }
    frozenModeRef.current = null;
    onClose?.();
  };

  const runWrite = async ({ intent, activeMode }) => {
    if (saving) return;
    const requestId = saveGateRef.current.next();
    // Capture the manager instance for this submit so session replacement cannot
    // redirect cleanup onto a newer visit's attempt object.
    const writeAttempt = writeAttemptRef.current;
    const ownership = {
      prospectId: openProspectIdRef.current || String(prospect.id || '').trim(),
      visitId: openVisitIdRef.current ?? visitId,
      authUid: openAuthUidRef.current ?? authUid,
    };
    setSaving(true);
    setError('');

    const transferSelections = selectedTransferIds();
    const createFingerprint = fingerprintAdminProspectAnalysisConversionCreatePayload({
      prospectId: prospect.id,
      version,
      transferSelections,
    });
    const linkFingerprint = fingerprintAdminProspectAnalysisConversionLinkPayload({
      prospectId: prospect.id,
      propertyId: selectedPropertyId,
      version,
    });

    const outcome = await runConversionWriteAttempt({
      intent,
      writeAttempt,
      formReady: activeMode === 'create' ? createReady : linkReady,
      fingerprint: activeMode === 'create' ? createFingerprint : linkFingerprint,
      build:
        activeMode === 'create'
          ? (operationId) =>
              buildCreateAdminProspectAnalysisPropertyRequest({
                operationId,
                prospectId: prospect.id,
                version,
                transferSelections,
              })
          : (operationId) =>
              buildLinkAdminProspectAnalysisPropertyRequest({
                operationId,
                prospectId: prospect.id,
                propertyId: selectedPropertyId,
                version,
              }),
      submit:
        activeMode === 'create'
          ? (payload) => createAdminProspectAnalysisProperty(payload)
          : (payload) => linkAdminProspectAnalysisProperty(payload),
      classifyError: classifyAdminCallableError,
    });

    if (!mountedRef.current || !saveGateRef.current.isCurrent(requestId)) return;
    if (!stillOwnsSession(ownership.prospectId, ownership.visitId, ownership.authUid)) return;

    if (outcome.ok) {
      setUncertainRetry(false);
      frozenModeRef.current = null;
      handleConvertedSuccess(
        outcome.result,
        activeMode,
        ownership,
        outcome.payload?.operationId || null,
      );
      return;
    }

    if (outcome.reason === 'form_not_ready' || outcome.reason === 'no_attempt') {
      setSaving(false);
      return;
    }

    if (outcome.reason === 'attempt_locked') {
      setUncertainRetry(true);
      setError(t('admin.prospection.analysisConversion.error.uncertainLocked'));
      setSaving(false);
      return;
    }

    const kind = outcome.errorKind || 'error';
    setError(t(adminProspectAnalysisConversionErrorKey(kind)));
    const uncertain = outcome.uncertainRetry === true || isConversionWriteUncertainError(kind);
    setUncertainRetry(uncertain);
    if (uncertain) {
      frozenModeRef.current = activeMode;
    } else {
      frozenModeRef.current = null;
    }
    setSaving(false);
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    if (uncertainRetry || !createReady || saving) return;
    await runWrite({ intent: 'prepare', activeMode: 'create' });
  };

  const handleLink = async (event) => {
    event.preventDefault();
    if (uncertainRetry || !linkReady || saving) return;
    await runWrite({ intent: 'prepare', activeMode: 'link' });
  };

  const handleRetryFrozen = async (event) => {
    event.preventDefault();
    if (!uncertainRetry || saving) return;
    const activeMode = frozenModeRef.current || mode;
    await runWrite({ intent: 'retry', activeMode });
  };

  const handleClose = () => {
    if (saving) return;
    writeAttemptRef.current = createAdminProspectAnalysisConversionWriteAttempt();
    frozenModeRef.current = null;
    onClose?.();
  };

  const fieldClass =
    'mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60';

  const municipalSection = preview.transferSections.municipalAssessment;
  const ownerExpenseSection = preview.transferSections.ownerExpenseTaxes;
  const addressSection = preview.transferSections.address;

  const uncertainBanner = uncertainRetry ? (
    <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="status">
      {t('admin.prospection.analysisConversion.uncertain.locked')}
    </p>
  ) : null;

  const renderErrorWithRetry = () =>
    error ? (
      <p id={errorId} className="text-sm text-red-700" role="alert">
        {error}
        {uncertainRetry ? (
          <button
            type="button"
            className="ml-2 underline"
            disabled={saving}
            onClick={(event) => void handleRetryFrozen(event)}
          >
            {t('admin.prospection.analysisConversion.retry')}
          </button>
        ) : null}
      </p>
    ) : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-5 shadow-lg">
        <h2 id={titleId} className="text-lg font-semibold text-slate-900">
          {t('admin.prospection.analysisConversion.title')}
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          {t('admin.prospection.analysisConversion.subtitle')}
        </p>
        {uncertainBanner}

        <div
          className="mt-4 flex gap-1 rounded-lg bg-slate-100 p-1"
          role="tablist"
          aria-label={t('admin.prospection.analysisConversion.modes')}
        >
          <button
            ref={firstRef}
            type="button"
            id={tabCreateId}
            role="tab"
            aria-selected={mode === 'create'}
            aria-controls={panelCreateId}
            disabled={controlsLocked}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 ${
              mode === 'create' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
            onClick={() => handleModeChange('create')}
          >
            {t('admin.prospection.analysisConversion.mode.create')}
          </button>
          <button
            type="button"
            id={tabLinkId}
            role="tab"
            aria-selected={mode === 'link'}
            aria-controls={panelLinkId}
            disabled={controlsLocked}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 ${
              mode === 'link' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
            onClick={() => handleModeChange('link')}
          >
            {t('admin.prospection.analysisConversion.mode.link')}
          </button>
        </div>

        {mode === 'create' ? (
          <form
            id={panelCreateId}
            role="tabpanel"
            aria-labelledby={tabCreateId}
            onSubmit={handleCreate}
            className="mt-4 space-y-3"
            noValidate
            aria-describedby={error ? errorId : undefined}
          >
            <p className="text-sm text-slate-600">
              {t('admin.prospection.analysisConversion.create.pricesNote')}
            </p>
            <dl className="grid gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  {t('admin.prospection.analysisConversion.preview.address')}
                </dt>
                <dd className="mt-0.5 break-words text-sm text-slate-900">
                  {displayValue(preview.address)}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  {t('admin.prospection.analysisConversion.preview.city')}
                </dt>
                <dd className="mt-0.5 break-words text-sm text-slate-900">
                  {displayValue(preview.city)}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  {t('admin.prospection.analysisConversion.preview.province')}
                </dt>
                <dd className="mt-0.5 break-words text-sm text-slate-900">
                  {preview.province
                    ? t(`admin.prospection.province.${preview.province}`)
                    : ADMIN_DATE_PLACEHOLDER}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  {t('admin.prospection.analysisConversion.preview.postalCode')}
                </dt>
                <dd className="mt-0.5 break-words text-sm text-slate-900">
                  {displayValue(preview.postalCode)}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  {t('admin.prospection.analysisConversion.preview.units')}
                </dt>
                <dd className="mt-0.5 break-words text-sm text-slate-900">
                  {formatAdminProspectUnits(preview.numberOfUnits)}
                </dd>
              </div>
            </dl>

            <fieldset className="space-y-3 rounded-lg border border-slate-200 p-3" disabled={controlsLocked}>
              <legend className="px-1 text-sm font-semibold text-slate-800">
                {t('admin.prospection.analysisConversion.transfer.title')}
              </legend>
              <div className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">
                <p className="font-medium">
                  {t('admin.prospection.analysisConversion.transfer.address.always')}
                </p>
                {!addressSection.available ? (
                  <p className="mt-1 text-amber-800" role="status">
                    {t('admin.prospection.analysisConversion.transfer.address.missing')}
                  </p>
                ) : null}
              </div>

              <label className="flex items-start gap-2 text-sm text-slate-800">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={transferMunicipal}
                  disabled={controlsLocked || !municipalSection.available}
                  onChange={(event) => setTransferMunicipal(event.target.checked)}
                />
                <span className="min-w-0">
                  <span className="block font-medium">
                    {t('admin.prospection.analysisConversion.transfer.municipalAssessment')}
                  </span>
                  {municipalSection.available ? (
                    <span className="block text-slate-600">
                      {formatCents(municipalSection.totalCents)}
                    </span>
                  ) : (
                    <span className="block text-amber-800">
                      {t('admin.prospection.analysisConversion.transfer.municipalAssessment.missing')}
                    </span>
                  )}
                </span>
              </label>

              <label className="flex items-start gap-2 text-sm text-slate-800">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={transferOwnerExpenses}
                  disabled={controlsLocked || !ownerExpenseSection.available}
                  onChange={(event) => setTransferOwnerExpenses(event.target.checked)}
                />
                <span className="min-w-0">
                  <span className="block font-medium">
                    {t('admin.prospection.analysisConversion.transfer.ownerExpenseTaxes')}
                  </span>
                  {ownerExpenseSection.available ? (
                    <ul className="mt-1 space-y-0.5 text-slate-600">
                      <li>
                        {t('admin.prospection.analysisConversion.transfer.field.municipalTaxes')}:{' '}
                        {formatCents(ownerExpenseSection.fields.municipalTaxes)}
                      </li>
                      <li>
                        {t('admin.prospection.analysisConversion.transfer.field.schoolTaxes')}:{' '}
                        {formatCents(ownerExpenseSection.fields.schoolTaxes)}
                      </li>
                      <li>
                        {t('admin.prospection.analysisConversion.transfer.field.insurance')}:{' '}
                        {formatCents(ownerExpenseSection.fields.insurance)}
                      </li>
                      <li>
                        {t('admin.prospection.analysisConversion.transfer.field.electricityHeating')}:{' '}
                        {formatCents(ownerExpenseSection.fields.electricityHeating)}
                      </li>
                      <li>
                        {t('admin.prospection.analysisConversion.transfer.field.otherExpenses')}:{' '}
                        {formatCents(ownerExpenseSection.fields.otherExpenses)}
                      </li>
                    </ul>
                  ) : (
                    <span className="block text-amber-800">
                      {t('admin.prospection.analysisConversion.transfer.ownerExpenseTaxes.missing')}
                    </span>
                  )}
                </span>
              </label>

              <div className="rounded-md border border-dashed border-slate-200 px-3 py-2 text-sm text-slate-600">
                <p className="font-medium text-slate-700">
                  {t('admin.prospection.analysisConversion.transfer.applicationDefaults')}
                </p>
                <ul className="mt-1 space-y-0.5">
                  <li>
                    {t('admin.prospection.analysisConversion.transfer.default.maintenance')}:{' '}
                    {preview.applicationDefaults.maintenance}
                  </li>
                  <li>
                    {t('admin.prospection.analysisConversion.transfer.default.concierge')}:{' '}
                    {preview.applicationDefaults.concierge}
                  </li>
                  <li>
                    {t('admin.prospection.analysisConversion.transfer.default.managementRate')}:{' '}
                    {preview.applicationDefaults.managementRate}%
                  </li>
                  <li>
                    {t('admin.prospection.analysisConversion.transfer.default.vacancyRate')}:{' '}
                    {preview.applicationDefaults.vacancyRate}%
                  </li>
                </ul>
              </div>
            </fieldset>

            {!version ? (
              <p className="text-sm text-amber-800" role="status">
                {t('admin.prospection.analysisConversion.error.versionRequired')}
              </p>
            ) : null}
            {renderErrorWithRetry()}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
                disabled={saving}
                onClick={handleClose}
              >
                {t('admin.prospection.analysisConversion.cancel')}
              </button>
              {uncertainRetry ? (
                <button
                  type="button"
                  className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
                  disabled={saving}
                  onClick={(event) => void handleRetryFrozen(event)}
                >
                  {saving
                    ? t('admin.prospection.analysisConversion.saving')
                    : t('admin.prospection.analysisConversion.retry')}
                </button>
              ) : (
                <button
                  type="submit"
                  className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
                  disabled={saving || !createReady}
                >
                  {saving
                    ? t('admin.prospection.analysisConversion.saving')
                    : t('admin.prospection.analysisConversion.create.submit')}
                </button>
              )}
            </div>
          </form>
        ) : (
          <form
            id={panelLinkId}
            role="tabpanel"
            aria-labelledby={tabLinkId}
            onSubmit={handleLink}
            className="mt-4 space-y-3"
            noValidate
            aria-describedby={error ? errorId : undefined}
          >
            <label className="block text-sm font-medium text-slate-700" htmlFor="admin-prospect-analysis-link-filter">
              {t('admin.prospection.analysisConversion.link.filter')}
              <input
                id="admin-prospect-analysis-link-filter"
                name="addressPrefixDraft"
                type="search"
                autoComplete="off"
                value={addressDraft}
                disabled={controlsLocked || listStatus === 'loading'}
                onChange={(event) => setAddressDraft(event.target.value)}
                className={fieldClass}
                placeholder={t('admin.prospection.analysisConversion.link.filterPlaceholder')}
              />
            </label>
            {appliedAddressPrefix ? (
              <p className="text-xs text-slate-500" data-testid="applied-address-prefix">
                {t('admin.prospection.analysisConversion.link.appliedPrefix')}: {appliedAddressPrefix}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
                disabled={controlsLocked || listStatus === 'loading'}
                onClick={() => {
                  void loadCandidates({ append: false });
                }}
              >
                {t('admin.prospection.analysisConversion.link.search')}
              </button>
            </div>
            {listStatus === 'loading' ? (
              <p className="text-sm text-slate-600" role="status">
                {t('admin.prospection.analysisConversion.link.loading')}
              </p>
            ) : null}
            {listError ? (
              <p className="text-sm text-red-700" role="alert">
                {listError}
              </p>
            ) : null}
            {listStatus === 'ready' && candidates.length === 0 ? (
              <p className="text-sm text-slate-600" role="status">
                {t('admin.prospection.analysisConversion.link.empty')}
              </p>
            ) : null}
            {listContinuationEmpty ? (
              <p className="text-sm text-slate-600" role="status">
                {t('admin.prospection.analysisConversion.link.continuationEmpty')}
              </p>
            ) : null}
            {candidates.length > 0 ? (
              <fieldset disabled={controlsLocked} className="space-y-2">
                <legend className="sr-only">
                  {t('admin.prospection.analysisConversion.link.select')}
                </legend>
                <ul className="max-h-56 space-y-2 overflow-y-auto rounded-lg border border-slate-200 p-2">
                  {candidates.map((item) => {
                    const itemId = item?.id ? String(item.id) : '';
                    if (!itemId) return null;
                    const inputId = `admin-prospect-analysis-candidate-${itemId}`;
                    const label = [item.address, item.city, item.postalCode]
                      .filter((part) => typeof part === 'string' && part.trim())
                      .join(', ');
                    return (
                      <li key={itemId}>
                        <label
                          htmlFor={inputId}
                          className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 hover:bg-slate-50"
                        >
                          <input
                            id={inputId}
                            type="radio"
                            name="analysisPropertyId"
                            value={itemId}
                            checked={selectedPropertyId === itemId}
                            disabled={controlsLocked}
                            onChange={() => setSelectedPropertyId(itemId)}
                            className="mt-1"
                          />
                          <span className="min-w-0 text-sm text-slate-800">
                            <span className="block font-medium">{label || itemId}</span>
                            <span className="block text-xs text-slate-500">{itemId}</span>
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </fieldset>
            ) : null}
            {nextPageToken ? (
              <button
                type="button"
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
                disabled={controlsLocked || listStatus === 'loading-more'}
                onClick={() => void loadCandidates({ append: true, pageToken: nextPageToken })}
              >
                {listStatus === 'loading-more'
                  ? t('admin.prospection.analysisConversion.link.loading')
                  : t('admin.prospection.analysisConversion.link.loadMore')}
              </button>
            ) : null}
            {!version ? (
              <p className="text-sm text-amber-800" role="status">
                {t('admin.prospection.analysisConversion.error.versionRequired')}
              </p>
            ) : null}
            {renderErrorWithRetry()}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
                disabled={saving}
                onClick={handleClose}
              >
                {t('admin.prospection.analysisConversion.cancel')}
              </button>
              {uncertainRetry ? (
                <button
                  type="button"
                  className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
                  disabled={saving}
                  onClick={(event) => void handleRetryFrozen(event)}
                >
                  {saving
                    ? t('admin.prospection.analysisConversion.saving')
                    : t('admin.prospection.analysisConversion.retry')}
                </button>
              ) : (
                <button
                  type="submit"
                  className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
                  disabled={saving || !linkReady}
                >
                  {saving
                    ? t('admin.prospection.analysisConversion.saving')
                    : t('admin.prospection.analysisConversion.link.submit')}
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
